import { integerFormatter } from '@utils/common/formatter';
import { chartColorCodes } from '@configs/chart';
import {
  ColumnChartDataModel,
  ColumnChartSeriesModel,
  AverageRatingDataModel,
  PieChartDataModel,
  WordCloudDataModel,
} from '@models/highChartsModel';
import { CommonUtil } from '@utils/common/common';
import { BaseChart } from '@classes/options/chart/baseChart';

const convertNum = (starString) => {
  switch (starString) {
    case 'star5':
      return '5';
    case 'star4':
      return '4';
    case 'star3':
      return '3';
    case 'star2':
      return '2';
    case 'star1':
      return '1';
    default:
      break;
  }
};
export class CreateReviewChartOption {
  colorCodes: { key: string; value: string }[];
  constructor() {
    this.colorCodes = chartColorCodes;
  }

  /**
   * @description Daily Review Count 차트 옵션
   * @param data
   * @param callbackFn
   * @returns
   */
  dailyReviewCount(data: any, callbackFn: (item: any) => void): ColumnChartDataModel {
    let series: ColumnChartSeriesModel[] = [];
    let categories = [];
    const _data = CommonUtil.getArrayByValidate(data);
    if (_data.length > 0) {
      const seriesNameList = Object.keys(_data[0]).filter((item) => item !== 'baseDate');
      categories = _data.map((item) => item.baseDate);
      series = seriesNameList.map((seriesName) => {
        return {
          name: seriesName,
          color: this.colorCodes.find((colorObj) => colorObj.key === convertNum(seriesName)).value,
          data: data.map((x) => x[`${seriesName}`]),
          point: {
            events: {
              click: function (e) {
                callbackFn(e.point.category);
              },
            },
          },
        };
      });
    }

    return {
      series,
      xAxis: {
        categories,
        labels: {
          overflow: 'justify',
          style: { color: 'blue', cursor: 'pointer' },
          events: {
            click: function (e) {
              callbackFn(this.value);
            },
          },
        },
      },
      legend: {
        useHTML: true,
        labelFormatter: function () {
          let point = this;
          let formatStr = '<strong>Rating</strong> ';
          for (let index = 0; index < parseInt(convertNum(point?.name)); index++) {
            formatStr += '<p class="btn-ico-single star-active"></p>';
          }
          return formatStr;
        },
      },
      tooltip: {
        useHTML: true,
        formatter: function () {
          const point = this.point;
          let formatStr = `<strong>${point.category}</strong>
              <br /><span style="color:${point.color}">●</span><strong>Rating</strong> `;
          for (
            let index = 0;
            index < parseInt(chartColorCodes.find((colorObj) => colorObj.value === point.color).key);
            index++
          ) {
            formatStr += '<p class="btn-ico-single star-active"></p>';
          }
          formatStr += `
              : <b>${integerFormatter(point.y)}</b>`;
          return formatStr;
        },
      },
    };
  }

  /**
   * @description Average Rating
   * @param data
   * @returns
   */
  averageRating(data): AverageRatingDataModel[] {
    const _data = CommonUtil.getArrayByValidate(data);
    const backGroundColor = ['#78c8a1', '#aed787', '#ffd834', '#ffb234', '#ff8b5a'];
    if (_data.length > 0) {
      const seriesNameList = Object.keys(_data[0]).filter((item) => item !== 'baseDate');
      let tempSum = {};
      let resultSum = [];
      // sorting 처리 5-4-3-2-1 순으로 정렬처리
      seriesNameList.sort((a, b) => parseInt(b[b.length - 1]) - parseInt(a[a.length - 1]));
      // 총 합계 객체 만들기
      seriesNameList.forEach((seriesName) => {
        tempSum[`${seriesName}`] = 0;
      });
      // 총 합계 처리
      data.forEach((dataObj) => {
        seriesNameList.forEach((seriesName) => {
          const tempValue = +dataObj[seriesName];
          tempSum[`${seriesName}`] = tempSum[`${seriesName}`] + tempValue;
        });
      });

      for (const property in tempSum) {
        let resultTemp = {};
        resultTemp[property] = tempSum[property];
        resultSum.push(resultTemp);
      }

      const chartData = resultSum.map((ratingObj, index) => {
        return {
          ...ratingObj,
          background: backGroundColor[index],
        };
      });
      return chartData;
    } else {
      return Array.from(Array(5).keys(), (x) => x + 1)
        .sort((a, b) => b - a)
        .map((item, index) => ({
          [`star${item}`]: 0,
          background: backGroundColor[index],
        }));
    }
  }

  /**
   * @description Reviews 차트
   * @param data
   * @returns
   */
  reviews(data): PieChartDataModel {
    const _data = CommonUtil.getArrayByValidate(data);
    let pieData = [];
    let tempSum = {};
    let totalCount = 0;

    if (_data.length > 0) {
      const seriesNameList = Object.keys(_data[0]).filter((item) => item !== 'baseDate');

      // sorting 처리 5-4-3-2-1 순으로 정렬처리
      seriesNameList.sort((a, b) => parseInt(b[b.length - 1]) - parseInt(a[a.length - 1]));
      // 총 합계 객체 만들기
      seriesNameList.forEach((seriesName) => {
        tempSum[`${seriesName}`] = 0;
      });

      // 총 합계 처리
      data.forEach((dataObj) => {
        seriesNameList.forEach((seriesName) => {
          const tempValue = +dataObj[seriesName];
          tempSum[`${seriesName}`] = tempSum[`${seriesName}`] + tempValue;
        });
      });
      // console.log('tempSum: ', tempSum);

      for (const property in tempSum) {
        let temp = {};
        temp['name'] = convertNum(property);
        temp['y'] = tempSum[property];
        temp['color'] = chartColorCodes.find((colorObj) => colorObj.key === convertNum(property)).value;
        totalCount = totalCount + tempSum[property];
        pieData.push(temp);
      }
    } else {
      pieData = [
        { name: '5', y: 0, color: '#78c8a1' },
        { name: '4', y: 0, color: '#aed787' },
        { name: '3', y: 0, color: '#ffd834' },
        { name: '2', y: 0, color: '#ffb234' },
        { name: '1', y: 0, color: '#ff8b5a' },
      ];
    }
    return {
      title: {
        text: `Total<br>${integerFormatter(totalCount)}`,
        align: 'center',
        verticalAlign: 'middle',
        y: 10,
        x: 80,
      },
      legend: {
        align: 'left',
        verticalAlign: 'middle',
        layout: 'vertical',
        useHTML: true,
        labelFormatter: function () {
          let point = this;
          // console.log('point: ', point);
          let formatStr = '<strong>Rating</strong> ';

          for (let index = 0; index < parseInt(point?.name); index++) {
            formatStr += '<p class="btn-ico-single star-active"></p>';
          }
          // console.log('formatStr: ', formatStr);
          return formatStr;
        },
      },
      plotOptions: {
        pie: {
          dataLabels: {
            enabled: false,
          },
          size: 130,
          innerSize: '50%',
          center: ['50%', '40%'],
          showInLegend: true,
        },
      },
      tooltip: new BaseChart().pieStarRatingTooltipOption(),
      series: [{ data: pieData, name: 'Reviews' }],
    };
  }

  /**
   * @description WordCloud 차트
   * @param data
   * @param seriesName selectedDate 선택한 날짜
   * @returns
   */
  wordcloud(data, seriesName): WordCloudDataModel {
    const _data = CommonUtil.getArrayByValidate(data);
    return {
      series:
        !_data || !seriesName
          ? []
          : _data?.length === 0
          ? _data
          : [
              {
                type: 'wordcloud',
                data: _data,
                name: seriesName,
              },
            ],
    };
  }
}
